import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { BACKLOG_CATEGORIES } from "../types";
import type { BacklogEntry, BacklogEntryFormData } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BacklogEntryFormData) => void;
  submitting: boolean;
  /** When set — edit mode. */
  entry?: BacklogEntry | null;
}

const EMPTY: BacklogEntryFormData = {
  title: "",
  description: "",
  category: "contracts_sales",
  is_urgent: false,
  discussion_date: null,
};

export function EntryFormModal({ open, onClose, onSubmit, submitting, entry }: Props) {
  const [form, setForm] = useState<BacklogEntryFormData>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(
      entry
        ? {
            title: entry.title,
            description: entry.description,
            category: entry.category,
            is_urgent: entry.is_urgent,
            discussion_date: entry.discussion_date,
          }
        : EMPTY,
    );
  }, [open, entry]);

  const canSubmit = form.title.trim().length > 0 && !submitting;

  return (
    <Modal open={open} onClose={onClose} title={entry ? "Edytuj temat" : "Dodaj temat"}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit({ ...form, title: form.title.trim() });
        }}
      >
        <Input
          label="Tytuł"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="np. Podjęcie współpracy z nowym podwykonawcą"
          autoFocus
        />
        <div className="flex flex-col gap-1">
          <label
            htmlFor="backlog-description"
            className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]"
          >
            Opis tematu
          </label>
          <textarea
            id="backlog-description"
            className="input-base min-h-[120px]"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Kontekst, oczekiwana decyzja, potencjalna wartość lub ryzyko…"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Kategoria"
            value={form.category}
            onChange={(e) =>
              setForm({ ...form, category: e.target.value as BacklogEntryFormData["category"] })
            }
            options={BACKLOG_CATEGORIES}
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="backlog-discussion-date" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Termin omówienia (opcjonalnie)
            </label>
            <DatePicker
              id="backlog-discussion-date"
              value={form.discussion_date ?? ""}
              onChange={(v) => setForm({ ...form, discussion_date: v || null })}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-primary)] cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_urgent}
            onChange={(e) => setForm({ ...form, is_urgent: e.target.checked })}
          />
          Pilne — na najbliższe omówienie
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit} loading={submitting}>
            {entry ? "Zapisz" : "Dodaj"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
